import {
  Link,
  createFileRoute,
  notFound,
  redirect,
} from '@tanstack/react-router'
import Pagination from '@/components/editorial/Pagination'
import { PAGE_SIZE, pageUrl, validatePageSearch } from '@/lib/pagination'
import { ArrowUpRight } from 'lucide-react'
import EditorialHero from '@/components/editorial/EditorialHero'
import EditorialPostList from '@/components/editorial/EditorialPostList'
import SectionHeader from '@/components/editorial/SectionHeader'
import Reveal from '@/components/preview/Reveal'
import { getTagPageData } from '@/lib/postServerFns'
import { isIndexableTopic, topicIntroductions } from '@/lib/topicSeo'

export const Route = createFileRoute('/blog/tag/$tag')({
  validateSearch: validatePageSearch,
  loaderDeps: ({ search }) => search,
  loader: async ({ params: { tag }, deps }) => {
    if (deps.page === 0) throw notFound()
    if (deps.page === 1)
      throw redirect({ to: '/blog/tag/$tag', params: { tag }, statusCode: 301 })
    const data = await getTagPageData({ data: { tag, page: deps.page } })
    if (!data.validPage || data.totalItems === 0) throw notFound()
    return { ...data, markdownSearch: deps }
  },
  component: TagPage,
  head: ({ loaderData }) => {
    if (!loaderData) {
      return {
        meta: [
          { title: '404 — Tag Not Found | Suriyaprakash Somu' },
          { name: 'robots', content: 'noindex, follow' },
          {
            name: 'description',
            content: 'The requested blog tag does not exist.',
          },
          { property: 'og:title', content: '404 — Tag Not Found' },
          {
            property: 'og:description',
            content: 'The requested blog tag does not exist.',
          },
          { name: 'twitter:title', content: '404 — Tag Not Found' },
          {
            name: 'twitter:description',
            content: 'The requested blog tag does not exist.',
          },
        ],
      }
    }

    const { tag, posts, page, totalItems } = loaderData
    const tagUrl = pageUrl(
      `https://www.suriyaprakash.in/blog/tag/${encodeURIComponent(tag)}`,
      page,
    )
    const tagKeywords = [
      tag,
      ...posts.flatMap((p) => p.frontmatter.tags),
      'blog',
    ]
      .filter((value, index, self) => self.indexOf(value) === index)
      .slice(0, 12)
      .join(', ')
    const description = `Articles tagged "${tag}" by Suriyaprakash Somu — ${totalItems} ${totalItems === 1 ? 'post' : 'posts'} exploring ${tag}, web development concepts and implementation tradeoffs.${page > 1 ? ` Page ${page} of ${loaderData.totalPages}.` : ''}`
    const title = `Posts tagged "${tag}"${page > 1 ? ` — Page ${page}` : ''} | Blog | Suriyaprakash Somu`
    const aiSummary = `This topic contains ${totalItems} ${totalItems === 1 ? 'article' : 'articles'} by Suriyaprakash Somu tagged "${tag}". Page ${page} of ${loaderData.totalPages}. The articles explain ${tag} through practical production web-development concepts, decisions, and implementation details.`

    return {
      meta: [
        {
          title,
        },
        {
          name: 'title',
          content: title,
        },
        {
          name: 'description',
          content: description,
        },
        {
          name: 'robots',
          content: isIndexableTopic(tag, totalItems)
            ? 'index, follow, max-image-preview:large'
            : 'noindex, follow',
        },
        { name: 'author', content: 'Suriyaprakash Somu' },
        { name: 'geo.region', content: 'IN-TN' },
        {
          name: 'geo.placename',
          content: 'Erode, Tamil Nadu, India',
        },
        { name: 'ai-summary', content: aiSummary },
        {
          name: 'keywords',
          content: tagKeywords,
        },
        {
          property: 'og:title',
          content: title,
        },
        {
          property: 'og:description',
          content: description,
        },
        { property: 'og:url', content: tagUrl },
        { property: 'og:type', content: 'website' },
        {
          property: 'og:image',
          content: 'https://www.suriyaprakash.in/og/default.png',
        },
        {
          name: 'twitter:card',
          content: 'summary_large_image',
        },
        {
          name: 'twitter:image',
          content: 'https://www.suriyaprakash.in/og/default.png',
        },
        { name: 'twitter:title', content: title },
        { name: 'twitter:description', content: description },
      ],
      links: [
        {
          rel: 'canonical',
          href: tagUrl,
        },
        {
          rel: 'alternate',
          type: 'application/rss+xml',
          title: 'RSS',
          href: 'https://www.suriyaprakash.in/rss.xml',
        },
      ],
      scripts: [
        {
          type: 'application/ld+json',
          children: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'CollectionPage',
            name: title,
            description,
            url: tagUrl,
            isPartOf: {
              '@type': 'Blog',
              name: 'Suriyaprakash Somu Blog',
              url: 'https://www.suriyaprakash.in/blog',
            },
          }),
        },
        {
          type: 'application/ld+json',
          children: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'ItemList',
            name: `Articles tagged "${tag}"`,
            itemListElement: posts.map((post, index) => ({
              '@type': 'ListItem',
              position: (page - 1) * PAGE_SIZE + index + 1,
              url: `https://www.suriyaprakash.in/blog/${post.category}/${post.slug}`,
              name: post.frontmatter.title,
            })),
          }),
        },
      ],
    }
  },
})

function TagPage() {
  const { tag, posts, allTags, categories, page, totalPages, totalItems } =
    Route.useLoaderData()

  return (
    <div className="min-h-screen bg-ink text-cream-50">
      <EditorialHero
        back={{ to: '/blog', label: 'Back to Blog' }}
        eyebrow="Tag / Topic index"
        title="Posts tagged"
        accent={`“${tag}”`}
        description={
          topicIntroductions[tag] ??
          `${totalItems} ${totalItems === 1 ? 'article' : 'articles'} on full-stack development, web fundamentals and backend systems.`
        }
        meta={
          <Link
            to="/blog/tag"
            className="preview-link-sweep preview-mono focus-ring inline-flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.2em] text-sage-200"
          >
            Browse all {allTags.length} tags
            <ArrowUpRight className="h-3.5 w-3.5" />
          </Link>
        }
      />

      <section className="bg-ink py-20 sm:py-28">
        <div className="container-custom">
          <SectionHeader
            eyebrow="Tagged notes"
            title="The"
            accent={tag}
            description="Browse the articles below or choose another topic."
            tone="dark"
          />

          <Reveal delay={100} className="mt-12 sm:mt-16">
            <details className="border-y border-white/15">
              <summary className="focus-ring cursor-pointer py-4 text-sm text-sage-200">
                Change topic — #{tag}
              </summary>
              <div className="flex flex-wrap">
                {allTags.map((topic) => (
                  <Link
                    key={topic}
                    to="/blog/tag/$tag"
                    params={{ tag: topic }}
                    aria-current={topic === tag ? 'page' : undefined}
                    className={`preview-mono focus-ring border-r border-white/15 px-4 py-4 text-[9px] font-bold uppercase tracking-[0.14em] transition-colors ${
                      topic === tag
                        ? 'bg-sage-200 text-forest-strong'
                        : 'text-cream-50/65 hover:bg-white/[0.05] hover:text-sage-200'
                    }`}
                  >
                    #{topic}
                  </Link>
                ))}
              </div>
            </details>
          </Reveal>

          <div className="mt-10 sm:mt-14">
            {posts.length > 0 ? (
              <EditorialPostList posts={posts} categories={categories} />
            ) : (
              <Reveal>
                <div className="border-y border-white/15 py-16">
                  <p className="font-heading text-3xl sm:text-5xl">
                    No articles use this tag yet.
                  </p>
                </div>
              </Reveal>
            )}
          </div>
          <Pagination
            page={page}
            totalPages={totalPages}
            to="/blog/tag/$tag"
            params={{ tag }}
          />
        </div>
      </section>
    </div>
  )
}
